import { createAction } from '@reduxjs/toolkit'
import { call, put, select, StrictEffect, takeLatest } from 'redux-saga/effects'

import { convertOpeningGroupsToDrawableLocations } from './data-prep/convertOpeningsToDrawableLocations'
import { regenerateMeasurementsByProjectAndDocumentIds } from './saveUpdatedCalibrationFactor'
import { regenerateJoistLines } from './updateSingleDocumentMapping'
import { FETCH_OPENING_GROUPS_SUCCESS } from '../../../actions/drawable'
import { acceptAutoScaleForDocumentChunk, fetchAutoScaleForDocumentChunk } from '../../../api/autoscale-api'
import { loadProjectBuildings } from '../../../api/projects-api'
import { fetchOpeningGroupsByProjectId } from '../../../api/takeoff-api'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import { AcceptAutoScaleResponse, PageScaleView } from '../../../models/autoScale'
import { DocumentChunk } from '../../../models/documentChunk'
import { DocumentMapping } from '../../../models/documentMapping'
import { Building } from '../../../models/projectBuilding'
import { Region } from '../../../models/region'
import { SNACKBAR_DURATION, SnackbarType } from '../../../shared/components/snackbar/constants'
import { AutoScaleOutcome } from '../../../shared/constants/auto-scale'
import { RootState } from '../../../stores'
import { convertScaleEnumToString } from '../../../utils/calculations/scaleConversion/scaleConversion'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import { drawAutoScaleOverlay, removeAutoScaleOverlay } from '../../lib/toolBoxes/2D/tools/autoscale/autoScaleOverlay'
import { preparedDrawablesSuccess } from '../../slices/2D'
import {
    selectAreAutoScaleDimensionsVisible,
    selectAutoScalePageView,
    setAutoScaleApplying,
    setAutoScalePageView,
    toggleAutoScaleDimensions,
} from '../../slices/autoScale'
import { setBuildings } from '../../slices/buildings'
import { CommonState, setSnackbarData } from '../../slices/common'
import {
    selectActiveDocumentChunk,
    selectActiveDocumentMapping,
    updateDocumentMappings,
    updateSingleDocumentChunk,
} from '../../slices/documents'
import { sceneRendered } from '../../slices/loading'
import { updateMappingFormState } from '../../slices/mappings'
import { selectProjectId } from '../../slices/project'
import { RegionFormFields, selectAllRegions, updateSingleRegion } from '../../slices/region'
import { setCalibrationRatio } from '../../slices/tools'
import { IMUP2DDrawableLocation } from '../../types'
import { pickLongestAutoScaleDims } from '../../utils/autoScale'

export const fetchAutoScaleForChunk = createAction<number>('fetchAutoScaleForChunk')
// refetches the open page without clearing it first, after a manual scale or calibration change
export const refreshAutoScaleForChunk = createAction<number>('refreshAutoScaleForChunk')
export const acceptAutoScale = createAction('acceptAutoScale')

const selectMappingFormState = (state: RootState): RegionFormFields => state.IMUP.mappings.mappingFormState

function* isChunkActive(documentChunkId: number): Generator<StrictEffect, boolean, any> {
    const activeChunk: DocumentChunk | null = yield select(selectActiveDocumentChunk)

    return activeChunk?.id === documentChunkId
}

const autoScaleSnackbar = (body: string, type: SnackbarType): NonNullable<CommonState['snackbarData']> => ({
    body,
    type,
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    autoHideDuration: SNACKBAR_DURATION.LONG,
})

export function* handleDrawAutoScaleDimensions(): Generator<StrictEffect, void, any> {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [workspaceTool]: [Workspace | undefined] = yield call(manager.getTools, [Workspace.NAME])

    if (!workspaceTool) return

    const paperScope: paper.PaperScope = yield call(workspaceTool.getPaperScope)
    const visible: boolean = yield select(selectAreAutoScaleDimensionsVisible)
    const view: PageScaleView | null = yield select(selectAutoScalePageView)

    if (!visible || !view) {
        yield call(removeAutoScaleOverlay, paperScope)

        return
    }

    yield call(drawAutoScaleOverlay, paperScope, pickLongestAutoScaleDims(view))
}

export function* watchForDrawAutoScaleDimensions() {
    yield takeLatest(
        [toggleAutoScaleDimensions.type, setAutoScalePageView.type, sceneRendered.type],
        handleDrawAutoScaleDimensions
    )
}

export function* handleFetchAutoScaleForChunk({
    type,
    payload: documentChunkId,
}: ReturnType<typeof fetchAutoScaleForChunk | typeof refreshAutoScaleForChunk>): Generator<StrictEffect, void, any> {
    const isRefresh = type === refreshAutoScaleForChunk.type

    if (!isRefresh) yield put(setAutoScalePageView(null))

    const projectId: number | null = yield select(selectProjectId)

    if (!projectId) return

    try {
        const view: PageScaleView | null = yield call(fetchAutoScaleForDocumentChunk, projectId, documentChunkId)
        const activeChunk: DocumentChunk | null = yield select(selectActiveDocumentChunk)

        // the person may have moved to another page while this loaded
        if (activeChunk?.id === documentChunkId) yield put(setAutoScalePageView(view))
    } catch (error) {
        yield call(console.error, error)

        // a result we could not confirm is worse than none: it may describe values the page no longer has
        if (isRefresh) yield put(setAutoScalePageView(null))
    }
}

export function* handleAcceptAutoScale(): Generator<StrictEffect, void, any> {
    const view: PageScaleView | null = yield select(selectAutoScalePageView)
    const activeChunk: DocumentChunk | null = yield select(selectActiveDocumentChunk)
    const activeMapping: DocumentMapping | null = yield select(selectActiveDocumentMapping)
    const projectId: number | null = yield select(selectProjectId)
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!view?.calculated || !activeChunk || !activeMapping || !projectId || view.documentChunkId !== activeChunk.id) {
        return
    }

    yield put(setAutoScaleApplying(true))

    try {
        const { documentMapping, documentChunk, regions }: AcceptAutoScaleResponse = yield call(
            acceptAutoScaleForDocumentChunk,
            projectId,
            activeChunk.id
        )
        const scale = documentMapping.scale_factor
        const scaleChanged = activeMapping.scale_factor !== scale

        yield put(updateDocumentMappings([{ ...activeMapping, ...documentMapping }]))

        // the person may navigate while the requests below are pending; ID-keyed store updates still apply to the
        // accepted page, but the form, canvas and page view belong to whichever page is active now
        if (scaleChanged) {
            if (yield call(isChunkActive, activeChunk.id)) {
                const mappingFormState: RegionFormFields = yield select(selectMappingFormState)

                yield put(updateMappingFormState({ ...mappingFormState, scale }))
            }

            const buildings: Building[] = yield call(loadProjectBuildings, projectId)

            yield put(setBuildings({ buildings }))
        }

        const storedRegions: Region[] = yield select(selectAllRegions)
        const changedRegions = regions.filter((region) =>
            storedRegions.some((stored) => stored.id === region.id && stored.scale !== region.scale)
        )

        if (changedRegions.length && manager) {
            const [workspaceTool]: [Workspace | undefined] = yield call(manager.getTools, [Workspace.NAME])

            for (const region of changedRegions) {
                yield put(updateSingleRegion(region))

                const regionItem: paper.Item | null =
                    workspaceTool && (yield call(isChunkActive, activeChunk.id))
                        ? yield call(workspaceTool.getItemWithRegionId, region.id)
                        : null

                if (workspaceTool && regionItem) {
                    yield call(
                        workspaceTool.updateScaleForRegion,
                        regionItem.id,
                        convertScaleEnumToString(region.scale)
                    )
                }
            }
        }

        yield put(updateSingleDocumentChunk(documentChunk))

        if (yield call(isChunkActive, activeChunk.id)) {
            yield put(setCalibrationRatio(documentChunk.calibration_factor_x))
        }

        if (manager && (yield call(isChunkActive, activeChunk.id))) {
            yield call(regenerateMeasurementsByProjectAndDocumentIds, manager, projectId, activeChunk.id)
        }

        const openingGroups: OpeningGroupAPI[] = yield call(fetchOpeningGroupsByProjectId, projectId)

        yield put({ type: FETCH_OPENING_GROUPS_SUCCESS, payload: openingGroups })

        const drawableLocations: IMUP2DDrawableLocation[] = yield call(
            convertOpeningGroupsToDrawableLocations,
            openingGroups
        )

        yield put(
            preparedDrawablesSuccess({
                drawableLocations,
                documentChunkIds: [...new Set(drawableLocations.map((location) => location.document_chunk_id))],
            })
        )

        if (scaleChanged) {
            yield call(
                regenerateJoistLines,
                openingGroups.filter((group) =>
                    group.openings.some((opening) =>
                        opening.opening_locations.some((location) => location.document_chunk_id === activeChunk.id)
                    )
                ),
                projectId,
                activeChunk.id
            )
        }

        if (yield call(isChunkActive, activeChunk.id)) {
            yield put(setAutoScalePageView({ ...view, outcome: AutoScaleOutcome.ACCEPTED }))
        }

        yield put(
            setSnackbarData(autoScaleSnackbar(`Scale set to ${convertScaleEnumToString(scale)}`, SnackbarType.SUCCESS))
        )
    } catch (error) {
        yield call(console.error, error)
        yield put(
            setSnackbarData(
                autoScaleSnackbar("Couldn't apply the suggested scale. Set the scale manually.", SnackbarType.ERROR)
            )
        )
    } finally {
        yield put(setAutoScaleApplying(false))
    }
}

export function* watchForFetchAutoScale() {
    yield takeLatest([fetchAutoScaleForChunk.type, refreshAutoScaleForChunk.type], handleFetchAutoScaleForChunk)
}

export function* watchForAcceptAutoScale() {
    yield takeLatest(acceptAutoScale.type, handleAcceptAutoScale)
}
